import { useState, useRef, useCallback, useEffect } from 'react';
import * as audioPlayer from './audio-player.js';
import { functionUrls } from './services/firebaseFunctions';
import * as audioCache from './audioCache.js';
import { logAudioCall } from './services/historyLogger';
import { loginMachine } from './auth/loginMachine';
import {
  DEFAULT_NARRATION_VOICE,
  GEMINI_LIVE_API_ENDPOINT,
  USER_GEMINI_API_KEY_STORAGE_KEY,
  buildNarrationPrompt,
  createGeminiLiveRealtimeTextPayload,
  createGeminiLiveSetupPayload,
  extractGeminiApiKey,
  looksLikeGeminiApiKey,
} from './packages/audio-narration-core';

const { generateCacheKey } = audioCache;

const ANECDOTE_MEMORY_STORAGE_KEY = 'curio.anecdote.memory.v1';
const TOPIC_TRANSCRIPT_MEMORY_STORAGE_KEY = 'curio.topic.transcript.memory.v1';
const MAX_ANECDOTES_PER_PLACE = 6;
const MAX_TRANSCRIPTS_PER_TOPIC = 8;
const MAX_KNOWN_TRANSCRIPT_SNIPPETS = 3;
const MAX_KNOWN_TRANSCRIPT_SNIPPET_CHARS = 260;
const MAX_AUDIO_LOG_TEXT_CHARS = 6000;

function isVerboseAudioLoggingEnabled() {
  if (typeof window === 'undefined') {
    return false;
  }

  try {
    const fromStorage = String(localStorage.getItem('curio.audio.verboseLogs') || '').trim().toLowerCase();
    if (fromStorage === '1' || fromStorage === 'true') {
      return true;
    }
  } catch (_) {}

  try {
    const queryValue = new URLSearchParams(window.location.search).get('audioVerboseLogs');
    if (queryValue === '1' || queryValue === 'true') {
      return true;
    }
  } catch (_) {}

  return false;
}

const AUDIO_VERBOSE_LOGS = isVerboseAudioLoggingEnabled();

function logAudioVerbose(...args) {
  if (!AUDIO_VERBOSE_LOGS) return;
  console.info(...args);
}

function sanitizeAudioLogText(text) {
  const normalized = String(text || '').replace(/\s+/g, ' ').trim();
  if (!normalized) return '';
  if (normalized.length <= MAX_AUDIO_LOG_TEXT_CHARS) return normalized;
  return `${normalized.slice(0, MAX_AUDIO_LOG_TEXT_CHARS)}…`;
}

function readUserGeminiApiKey() {
  if (typeof window === 'undefined') return '';
  try {
    const rawKey = String(localStorage.getItem(USER_GEMINI_API_KEY_STORAGE_KEY) || '').trim();
    const apiKey = extractGeminiApiKey(rawKey);
    if (apiKey && apiKey !== rawKey) {
      localStorage.setItem(USER_GEMINI_API_KEY_STORAGE_KEY, apiKey);
    }
    return apiKey;
  } catch (_) {
    return '';
  }
}

async function parseWebSocketJsonMessage(data) {
  if (typeof data === 'string') {
    return JSON.parse(data);
  }

  if (typeof Blob !== 'undefined' && data instanceof Blob) {
    return JSON.parse(await data.text());
  }

  if (typeof ArrayBuffer !== 'undefined' && data instanceof ArrayBuffer) {
    return JSON.parse(new TextDecoder('utf-8').decode(data));
  }

  if (typeof ArrayBuffer !== 'undefined' && ArrayBuffer.isView?.(data)) {
    return JSON.parse(new TextDecoder('utf-8').decode(data));
  }

  if (data && typeof data === 'object') {
    return data;
  }

  return JSON.parse(String(data || ''));
}

function buildAddressContext(locationData) {
  const address = locationData?.address || {};

  const pick = (...keys) => {
    for (const key of keys) {
      const value = (address?.[key] || '').toString().trim();
      if (value) return value;
    }
    return '';
  };

  return {
    topicCountry: (locationData?.topic_country || pick('country')).toString().trim(),
    topicSecondMostZoomedOutFeature: (
      locationData?.topic_second_most_zoomed_out_feature ||
      pick('state', 'region', 'province', 'state_district', 'county', 'city', 'town', 'village', 'hamlet')
    ).toString().trim(),
  };
}

function buildAnecdoteMemoryKey(topicTitle, languageCode, locationContext = {}) {
  const keyBits = [
    topicTitle,
    languageCode,
    locationContext?.topicSecondMostZoomedOutFeature,
    locationContext?.topicCountry,
  ]
    .map((v) => (v || '').toString().trim().toLowerCase())
    .filter(Boolean);

  return keyBits.join('||');
}

function readAnecdoteMemory() {
  try {
    const raw = localStorage.getItem(ANECDOTE_MEMORY_STORAGE_KEY);
    if (!raw) return {};
    const parsed = JSON.parse(raw);
    return parsed && typeof parsed === 'object' ? parsed : {};
  } catch {
    return {};
  }
}

function writeAnecdoteMemory(memory) {
  try {
    localStorage.setItem(ANECDOTE_MEMORY_STORAGE_KEY, JSON.stringify(memory || {}));
  } catch (_) {}
}

function getStoredAnecdotes(memoryKey) {
  if (!memoryKey) return [];
  const memory = readAnecdoteMemory();
  const list = memory[memoryKey];
  return Array.isArray(list) ? list.filter(Boolean) : [];
}

function storeAnecdote(memoryKey, anecdoteText) {
  if (!memoryKey || !anecdoteText) return;

  const cleaned = anecdoteText.toString().trim();
  if (!cleaned) return;

  const memory = readAnecdoteMemory();
  const existing = Array.isArray(memory[memoryKey]) ? memory[memoryKey] : [];

  if (existing.some((item) => item.toLowerCase() === cleaned.toLowerCase())) {
    return;
  }

  memory[memoryKey] = [cleaned, ...existing].slice(0, MAX_ANECDOTES_PER_PLACE);
  writeAnecdoteMemory(memory);
}

function buildTopicTranscriptMemoryKey(topicTitle, languageCode, locationContext = {}, selectedArticle = null, explicitTopicId = '') {
  const explicit = (explicitTopicId || '').toString().trim().toLowerCase();
  if (explicit) {
    return `topicId:${explicit}|lang:${(languageCode || '').toString().trim().toLowerCase()}`;
  }

  const articleKey = [
    selectedArticle?.sourceWikiLang,
    selectedArticle?.pageid,
    selectedArticle?.title,
  ]
    .map((v) => (v || '').toString().trim().toLowerCase())
    .filter(Boolean)
    .join('|');

  const keyBits = [
    topicTitle,
    languageCode,
    locationContext?.topicSecondMostZoomedOutFeature,
    locationContext?.topicCountry,
    articleKey,
  ]
    .map((v) => (v || '').toString().trim().toLowerCase())
    .filter(Boolean);

  return keyBits.join('||');
}

function readTopicTranscriptMemory() {
  try {
    const raw = localStorage.getItem(TOPIC_TRANSCRIPT_MEMORY_STORAGE_KEY);
    if (!raw) return {};
    const parsed = JSON.parse(raw);
    return parsed && typeof parsed === 'object' ? parsed : {};
  } catch {
    return {};
  }
}

function writeTopicTranscriptMemory(memory) {
  try {
    localStorage.setItem(TOPIC_TRANSCRIPT_MEMORY_STORAGE_KEY, JSON.stringify(memory || {}));
  } catch (_) {}
}

function getStoredTopicTranscripts(memoryKey) {
  if (!memoryKey) return [];
  const memory = readTopicTranscriptMemory();
  const list = memory[memoryKey];
  if (!Array.isArray(list)) return [];
  return list
    .map((item) => (item || '').toString().trim())
    .filter(Boolean);
}

function storeTopicTranscript(memoryKey, transcriptText) {
  if (!memoryKey || !transcriptText) return;

  const cleaned = transcriptText.toString().trim();
  if (!cleaned) return;

  const memory = readTopicTranscriptMemory();
  const existing = Array.isArray(memory[memoryKey]) ? memory[memoryKey] : [];

  if (existing.some((item) => item.toLowerCase() === cleaned.toLowerCase())) {
    return;
  }

  memory[memoryKey] = [cleaned, ...existing].slice(0, MAX_TRANSCRIPTS_PER_TOPIC);
  writeTopicTranscriptMemory(memory);
}

function buildKnownTranscriptSnippets(memoryKey) {
  const transcripts = getStoredTopicTranscripts(memoryKey);
  return transcripts
    .slice(0, MAX_KNOWN_TRANSCRIPT_SNIPPETS)
    .map((text) => text.slice(0, MAX_KNOWN_TRANSCRIPT_SNIPPET_CHARS).trim())
    .filter(Boolean);
}

export default function useAudioNarration(userLanguage = navigator.language) {
  const [status, setStatus] = useState('idle');
  const [title, setTitle] = useState('');
  const [mode, setMode] = useState('');
  const [isPlaying, setIsPlaying] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);
  const [currentTime, setCurrentTime] = useState(0);
  const [duration, setDuration] = useState(0);

  const languageCode = userLanguage;
  const historyAudioUrl = functionUrls.historyAudio;

  const esRef = useRef(null);
  const directWsRef = useRef(null);
  const statusRef = useRef(status);
  const pausedChunkBufferRef = useRef([]);

  const narrationPerfRef = useRef({
    requestStartedAt: 0,
    firstAudioAt: 0,
    firstTextAt: 0,
    firstAudioLogged: false,
    firstTextLogged: false,
    audioChunkCount: 0,
    textChunkCount: 0,
    transcriptChunkCount: 0,
    transport: '',
    requestId: '',
    metricsLogged: false,
  });

  const sseStreamIdRef = useRef(0);
  const activeNarrationRef = useRef(null);

  const cacheKeyRef = useRef('');
  const chunkIndexRef = useRef(0);
  const narrationTextRef = useRef('');
  const lastNarrationTextChunkRef = useRef('');

  const titleRef = useRef('');
  const modeRef = useRef('');

  const audioRef = useRef(null);
  const eventSourceRef = useRef(null);

  useEffect(() => {
    statusRef.current = status;
  }, [status]);

  const resetNarrationText = useCallback(() => {
    narrationTextRef.current = '';
    lastNarrationTextChunkRef.current = '';
  }, []);

  const appendNarrationText = useCallback((textChunk) => {
    const cleaned = (textChunk || '').toString().trim();
    if (!cleaned) return;

    if (lastNarrationTextChunkRef.current === cleaned) {
      return;
    }

    const existing = narrationTextRef.current.trim();
    narrationTextRef.current = existing ? `${existing} ${cleaned}` : cleaned;
    lastNarrationTextChunkRef.current = cleaned;
  }, []);

  const logNarrationPrompt = useCallback((request, transport) => {
    if (!request) return;
    console.info('[Audio][Prompt]', {
      requestId: request.requestId || null,
      transport: String(transport || '').trim() || 'unknown',
      mode: request.narrationMode || null,
      topicTitle: request.topicTitle || null,
      prompt: sanitizeAudioLogText(request.prompt),
      promptLength: String(request.prompt || '').length,
    });
  }, []);

  const logNarrationTranscript = useCallback((reason = 'complete') => {
    const active = activeNarrationRef.current;
    const transcript = sanitizeAudioLogText(narrationTextRef.current);
    console.info('[Audio][Transcript]', {
      reason,
      requestId: active?.requestId || null,
      mode: active?.narrationMode || null,
      topicTitle: active?.topicTitle || null,
      transcript,
      transcriptLength: String(narrationTextRef.current || '').trim().length,
      hasTranscript: !!transcript,
    });
  }, []);

  const resetNarrationPerf = useCallback((transport, requestId = '', startedAt = performance.now()) => {
    narrationPerfRef.current = {
      requestStartedAt: startedAt,
      firstAudioAt: 0,
      firstTextAt: 0,
      firstAudioLogged: false,
      firstTextLogged: false,
      audioChunkCount: 0,
      textChunkCount: 0,
      transcriptChunkCount: 0,
      transport: String(transport || '').trim() || 'unknown',
      requestId: String(requestId || '').trim(),
      metricsLogged: false,
    };
  }, []);

  const noteNarrationTextChunk = useCallback((isTranscript = false) => {
    const perf = narrationPerfRef.current;
    perf.textChunkCount += 1;
    if (isTranscript) {
      perf.transcriptChunkCount += 1;
    }

    if (!perf.firstTextLogged) {
      perf.firstTextLogged = true;
      perf.firstTextAt = performance.now();
      const elapsedMs = Math.round(perf.firstTextAt - perf.requestStartedAt);
      logAudioVerbose('[Audio][Perf] First text chunk received in', elapsedMs, 'ms');
    }
  }, []);

  const noteNarrationAudioChunk = useCallback(() => {
    const perf = narrationPerfRef.current;
    perf.audioChunkCount += 1;

    if (!perf.firstAudioLogged) {
      perf.firstAudioLogged = true;
      perf.firstAudioAt = performance.now();
      const elapsedMs = Math.round(perf.firstAudioAt - perf.requestStartedAt);
      console.info('[Audio][Perf] Click to first audio packet', {
        requestId: perf.requestId || activeNarrationRef.current?.requestId || null,
        transport: perf.transport,
        clickToFirstAudioPacketMs: elapsedMs,
      });
      logAudioVerbose('[Audio][Perf] First audio chunk received in', elapsedMs, 'ms');
    }
  }, []);

  const logNarrationPerfMetrics = useCallback((reason = 'complete') => {
    const perf = narrationPerfRef.current;
    if (perf.metricsLogged || !perf.requestStartedAt) {
      return;
    }
    perf.metricsLogged = true;

    const totalMs = Math.round(performance.now() - perf.requestStartedAt);
    const firstTextMs = perf.firstTextAt ? Math.round(perf.firstTextAt - perf.requestStartedAt) : null;
    const firstAudioMs = perf.firstAudioAt ? Math.round(perf.firstAudioAt - perf.requestStartedAt) : null;
    const firstTokenMs = Number.isFinite(firstTextMs) && Number.isFinite(firstAudioMs)
      ? Math.min(firstTextMs, firstAudioMs)
      : (Number.isFinite(firstTextMs) ? firstTextMs : (Number.isFinite(firstAudioMs) ? firstAudioMs : null));

    console.info('[Audio][Metrics]', {
      reason,
      requestId: perf.requestId || activeNarrationRef.current?.requestId || null,
      transport: perf.transport,
      totalMs,
      firstTokenMs,
      firstTextMs,
      firstAudioMs,
      textChunkCount: perf.textChunkCount,
      transcriptChunkCount: perf.transcriptChunkCount,
      audioChunkCount: perf.audioChunkCount,
      hasOutputTranscription: perf.transcriptChunkCount > 0,
    });
  }, []);

  const storeAnecdoteIfNeeded = useCallback(() => {
    const active = activeNarrationRef.current;
    if (!active || active.narrationMode !== 'anecdote') return;

    const text = narrationTextRef.current.trim();
    if (!text) return;

    storeAnecdote(active.anecdoteMemoryKey, text);
  }, []);

  const storeTopicTranscriptIfNeeded = useCallback(() => {
    const active = activeNarrationRef.current;
    if (!active) return;

    const text = narrationTextRef.current.trim();
    if (!text) return;

    storeTopicTranscript(active.topicTranscriptMemoryKey, text);
  }, []);

  useEffect(() => {
    audioPlayer.initialize();

    audioPlayer.setOnPlaybackComplete(() => {
      setStatus('idle');
      setIsPlaying(false);
      window.dispatchEvent(new CustomEvent('audioCompleted', {
        detail: {
          title: titleRef.current,
          mode: modeRef.current,
        },
      }));
    });

    audioPlayer.setOnProgressUpdate((nextCurrentTime, nextDuration) => {
      setCurrentTime(nextCurrentTime);
      setDuration(nextDuration);
    });

    return () => {
      try {
        esRef.current?.close();
      } catch (_) {}
      esRef.current = null;
    };
  }, []);

  const markCurrentNarrationComplete = useCallback(() => {
    const cacheKey = activeNarrationRef.current?.cacheKey || cacheKeyRef.current;
    if (!cacheKey) {
      return;
    }

    audioCache.markComplete(cacheKey)
      .catch((err) => console.error('[Audio] Failed to mark cache complete', err));
  }, []);

  const processRealtimeAudioDelta = useCallback((base64Pcm) => {
    try {
      const buf = audioPlayer.base64ToArrayBuffer(base64Pcm);

      if (statusRef.current === 'paused') {
        pausedChunkBufferRef.current.push(buf);
      } else {
        audioPlayer.playPCMAudio(buf);
        setIsPlaying(true);
        if (statusRef.current !== 'playing') {
          setStatus('playing');
        }
      }

      const activeCacheKey = activeNarrationRef.current?.cacheKey || cacheKeyRef.current;
      if (activeCacheKey) {
        const chunkIndex = chunkIndexRef.current++;
        audioCache.storeChunk(activeCacheKey, chunkIndex, buf)
          .catch((err) => console.error('[Audio] Failed to store chunk in cache', err));
      }
    } catch (err) {
      console.error('[Audio] Error processing realtime audio chunk', err);
    }
  }, []);

  const stopSseStream = useCallback((invalidate = false) => {
    if (invalidate) {
      sseStreamIdRef.current += 1;
    }
    try {
      esRef.current?.close();
    } catch (_) {}
    esRef.current = null;
    try {
      directWsRef.current?.close();
    } catch (_) {}
    directWsRef.current = null;
  }, []);

  // eslint-disable-next-line no-unused-vars
  const startSseNarration = useCallback((request, idToken) => {
    stopSseStream(true);
    const streamId = sseStreamIdRef.current;

    const params = new URLSearchParams({
      location: request.topicTitle,
      mode: request.narrationMode,
      languageCode: request.languageCode,
      authToken: idToken,
      requestId: request.requestId,
    });

    if (request.topicCountry) {
      params.append('topicCountry', request.topicCountry);
    }
    if (request.topicSecondMostZoomedOutFeature) {
      params.append('topicSecondMostZoomedOutFeature', request.topicSecondMostZoomedOutFeature);
    }
    if (Array.isArray(request.priorAnecdotes) && request.priorAnecdotes.length) {
      params.append('priorAnecdotesJson', JSON.stringify(request.priorAnecdotes));
    }

    Object.entries(request.extraParams || {}).forEach(([k, v]) => {
      if (k === 'priorAnecdotes' || k === 'knownTranscriptSnippets') return;
      if (v == null || v === '') return;
      params.append(k, typeof v === 'string' ? v : JSON.stringify(v));
    });

    const requestUrl = `${historyAudioUrl}?${params.toString()}`;
    logNarrationPrompt(request, 'sse');
    const es = new EventSource(requestUrl);

    esRef.current = es;
    activeNarrationRef.current = request;
    cacheKeyRef.current = request.cacheKey;
    chunkIndexRef.current = 0;

    pausedChunkBufferRef.current = [];
    resetNarrationText();
    audioPlayer.stopPlayback();
    audioPlayer.resetAudioState();

    resetNarrationPerf('sse', request.requestId, request.startedAt || performance.now());
    setStatus('connecting');

    es.addEventListener('audio', (evt) => {
      if (streamId !== sseStreamIdRef.current) {
        return;
      }
      noteNarrationAudioChunk();
      processRealtimeAudioDelta(evt.data);
    });

    es.addEventListener('transcript', (evt) => {
      if (streamId !== sseStreamIdRef.current) {
        return;
      }
      noteNarrationTextChunk(true);
      appendNarrationText(evt.data);
    });

    es.addEventListener('text', (evt) => {
      if (streamId !== sseStreamIdRef.current) {
        return;
      }
      const cleaned = (evt.data || '').toString().trim();
      if (cleaned && cleaned === lastNarrationTextChunkRef.current) {
        return;
      }
      noteNarrationTextChunk(false);
      appendNarrationText(evt.data);
    });

    es.addEventListener('done', () => {
      if (streamId !== sseStreamIdRef.current) {
        return;
      }
      logNarrationPerfMetrics('sse_done');
      audioPlayer.completeAudioStream();
      markCurrentNarrationComplete();
      storeAnecdoteIfNeeded();
      storeTopicTranscriptIfNeeded();
      logNarrationTranscript('sse_done');
      stopSseStream(true);

      if (!audioPlayer.isAudioPlaying()) {
        setStatus('idle');
        setIsPlaying(false);
      }
    });

    es.onopen = () => {
      if (streamId !== sseStreamIdRef.current) {
        return;
      }
      setStatus('streaming');
    };

    es.onerror = () => {
      if (streamId !== sseStreamIdRef.current) {
        return;
      }
      logNarrationPerfMetrics('sse_error');
      window.dispatchEvent(new Event('quotaExceeded'));
      stopSseStream(true);
      setStatus('idle');
    };
  }, [
    appendNarrationText,
    historyAudioUrl,
    logNarrationPerfMetrics,
    logNarrationPrompt,
    logNarrationTranscript,
    markCurrentNarrationComplete,
    noteNarrationAudioChunk,
    noteNarrationTextChunk,
    processRealtimeAudioDelta,
    resetNarrationPerf,
    resetNarrationText,
    stopSseStream,
    storeAnecdoteIfNeeded,
    storeTopicTranscriptIfNeeded,
  ]);

  const startDirectGeminiNarration = useCallback((request, userApiKey) => {
    stopSseStream(true);
    const streamId = sseStreamIdRef.current;
    const apiKey = extractGeminiApiKey(userApiKey);

    if (!apiKey) {
      setStatus('idle');
      setError('Missing Gemini API key');
      return;
    }

    if (!looksLikeGeminiApiKey(apiKey)) {
      console.warn('[Audio] Saved Gemini API key does not look valid', {
        length: apiKey.length,
        startsWithAIza: apiKey.startsWith('AIza'),
      });
      setStatus('idle');
      setError('Saved Gemini API key is not valid. Open the menu to change or clear it.');
      return;
    }

    logNarrationPrompt(request, 'direct-gemini-live');

    activeNarrationRef.current = request;
    cacheKeyRef.current = request.cacheKey;
    chunkIndexRef.current = 0;
    pausedChunkBufferRef.current = [];
    resetNarrationText();
    audioPlayer.stopPlayback();
    audioPlayer.resetAudioState();
    resetNarrationPerf('direct-gemini-live', request.requestId, request.startedAt || performance.now());
    setStatus('connecting');

    let ws;
    let setupComplete = false;
    let finished = false;
    let loggedFirstServerContentShape = false;
    let setupTimer = null;

    const cleanup = () => {
      if (setupTimer) {
        clearTimeout(setupTimer);
        setupTimer = null;
      }
      if (directWsRef.current === ws) {
        directWsRef.current = null;
      }
    };

    const finish = (reason = 'direct_done', { complete = false } = {}) => {
      if (finished || streamId !== sseStreamIdRef.current) return;
      finished = true;
      cleanup();
      logNarrationPerfMetrics(reason);
      if (complete) {
        audioPlayer.completeAudioStream();
        markCurrentNarrationComplete();
        storeAnecdoteIfNeeded();
        storeTopicTranscriptIfNeeded();
        logNarrationTranscript(reason);
      }

      try {
        ws?.close();
      } catch (_) {}

      if (!audioPlayer.isAudioPlaying()) {
        setStatus('idle');
        setIsPlaying(false);
      }
    };

    try {
      ws = new WebSocket(`${GEMINI_LIVE_API_ENDPOINT}?key=${encodeURIComponent(apiKey)}`);
      ws.binaryType = 'arraybuffer';
      directWsRef.current = ws;
    } catch (error) {
      console.error('[Audio] Failed to open direct Gemini Live websocket:', error);
      setError('could not connect to Gemini Live');
      setStatus('idle');
      return;
    }

    setupTimer = window.setTimeout(() => {
      if (finished || setupComplete || streamId !== sseStreamIdRef.current) return;
      console.error('[Audio] Direct Gemini Live setup timed out');
      setError('Gemini Live setup timed out');
      try {
        ws.close();
      } catch (_) {}
      cleanup();
      setStatus('idle');
    }, 15000);

    ws.onopen = () => {
      if (streamId !== sseStreamIdRef.current) return;
      try {
        ws.send(JSON.stringify(createGeminiLiveSetupPayload({
          languageCode: request.languageCode,
          voiceName: request.voice,
          enableGoogleSearch: request.enableGoogleSearch === true,
        })));
      } catch (error) {
        console.error('[Audio] Failed to send direct Gemini Live setup:', error);
        setError('could not start Gemini Live');
        try {
          ws.close();
        } catch (_) {}
        cleanup();
        setStatus('idle');
      }
    };

    ws.onmessage = async (event) => {
      if (finished || streamId !== sseStreamIdRef.current) return;

      let payload;
      try {
        payload = await parseWebSocketJsonMessage(event.data);
      } catch (error) {
        console.error('[Audio] Failed to parse direct Gemini Live payload:', error);
        setError('could not read Gemini Live response');
        finish('direct_parse_error');
        return;
      }

      if (finished || streamId !== sseStreamIdRef.current) return;

      const setupPayloadComplete = payload.setupComplete ?? payload.setup_complete;
      if (!setupComplete && (setupPayloadComplete === true || (setupPayloadComplete && typeof setupPayloadComplete === 'object'))) {
        setupComplete = true;
        if (setupTimer) {
          clearTimeout(setupTimer);
          setupTimer = null;
        }
        setStatus('streaming');
        try {
          ws.send(JSON.stringify(createGeminiLiveRealtimeTextPayload(request.prompt)));
        } catch (error) {
          console.error('[Audio] Failed to send direct Gemini Live prompt:', error);
          setError('could not start Gemini Live');
          finish('direct_prompt_send_error');
        }
        return;
      }

      if (payload.error) {
        console.error('[Audio] Direct Gemini Live error payload:', payload.error);
        setError('Gemini Live returned an error');
        finish('direct_error_payload');
        return;
      }

      const serverContent = payload?.serverContent || payload?.server_content;
      if (!serverContent || typeof serverContent !== 'object') {
        return;
      }

      if (serverContent.interrupted === true || serverContent.interrupted === 'true') {
        finish('direct_interrupted');
        return;
      }

      const outputTranscriptionText = typeof serverContent?.outputTranscription?.text === 'string'
        ? serverContent.outputTranscription.text
        : (typeof serverContent?.output_transcription?.text === 'string'
          ? serverContent.output_transcription.text
          : '');

      if (outputTranscriptionText) {
        noteNarrationTextChunk(true);
        appendNarrationText(outputTranscriptionText);
      }

      const modelTurn = serverContent.modelTurn || serverContent.model_turn;
      const parts = Array.isArray(modelTurn?.parts) ? modelTurn.parts : [];

      if (!loggedFirstServerContentShape) {
        loggedFirstServerContentShape = true;
        logAudioVerbose('[Audio] First direct Gemini serverContent shape', {
          serverContentKeys: Object.keys(serverContent),
          partCount: parts.length,
          partKeys: parts.slice(0, 4).map((part) => Object.keys(part || {})),
          outputTranscriptionChars: outputTranscriptionText.length,
        });
      }

      for (const part of parts) {
        const textPart = typeof part?.text === 'string' ? part.text.trim() : '';
        if (textPart && !outputTranscriptionText) {
          noteNarrationTextChunk(false);
          appendNarrationText(textPart);
        }

        const inlineData = part?.inlineData || part?.inline_data;
        const base64Audio = typeof inlineData?.data === 'string' ? inlineData.data : '';
        if (!base64Audio) continue;

        noteNarrationAudioChunk();
        processRealtimeAudioDelta(base64Audio);
      }

      const turnComplete = serverContent.turnComplete === true || serverContent.turn_complete === true;
      if (turnComplete) {
        finish('direct_done', { complete: true });
      }
    };

    ws.onerror = (event) => {
      if (finished || streamId !== sseStreamIdRef.current) return;
      console.error('[Audio] Direct Gemini Live websocket error:', event);
      setError('could not connect to Gemini Live');
      logNarrationPerfMetrics('direct_error');
      cleanup();
      setStatus('idle');
    };

    ws.onclose = (event) => {
      if (finished || streamId !== sseStreamIdRef.current) return;
      console.warn('[Audio] Direct Gemini Live websocket closed:', {
        code: event.code,
        reason: event.reason,
        setupComplete,
      });
      if (event.code === 1007 && /api key/i.test(event.reason || '')) {
        setError('Saved Gemini API key is not valid. Open the menu to change or clear it.');
      } else if (!setupComplete) {
        setError('could not connect to Gemini Live');
      }
      logNarrationPerfMetrics('direct_closed');
      cleanup();
      setStatus('idle');
    };
  }, [
    appendNarrationText,
    logNarrationPerfMetrics,
    logNarrationPrompt,
    logNarrationTranscript,
    markCurrentNarrationComplete,
    noteNarrationAudioChunk,
    noteNarrationTextChunk,
    processRealtimeAudioDelta,
    resetNarrationPerf,
    resetNarrationText,
    stopSseStream,
    storeAnecdoteIfNeeded,
    storeTopicTranscriptIfNeeded,
  ]);

  const stop = useCallback(() => {
    stopSseStream(true);

    audioPlayer.stopPlayback();
    audioPlayer.resetAudioState();
    pausedChunkBufferRef.current = [];

    setStatus('idle');
    setTitle('');
    setMode('');
    setCurrentTime(0);
    setDuration(0);
    setIsPlaying(false);
    setIsLoading(false);

    activeNarrationRef.current = null;
    cacheKeyRef.current = '';
    chunkIndexRef.current = 0;
  }, [stopSseStream]);

  const playAudioNarration = useCallback(async (articles) => {
    if (!articles || articles.length === 0) {
      setError('No articles provided for narration');
      return;
    }

    try {
      setIsLoading(true);
      setError(null);

      if (audioRef.current) {
        audioRef.current.pause();
        audioRef.current = null;
      }

      if (eventSourceRef.current) {
        eventSourceRef.current.close();
        eventSourceRef.current = null;
      }

      const idToken = await loginMachine.getIdToken();
      if (!idToken) {
        throw new Error('Authentication required');
      }

      const articlesData = articles.map((article) => ({
        title: article.title || '',
        extract: article.extract || '',
      }));

      const url = new URL(historyAudioUrl);
      url.searchParams.set('articles', JSON.stringify(articlesData));
      url.searchParams.set('authToken', idToken);

      eventSourceRef.current = new EventSource(url.toString());

      eventSourceRef.current.onmessage = (event) => {
        try {
          const data = JSON.parse(event.data);

          if (data.type === 'audio' && data.audio) {
            const audioBlob = base64ToBlob(data.audio, 'audio/mpeg');
            const audioUrl = URL.createObjectURL(audioBlob);

            audioRef.current = new Audio(audioUrl);
            audioRef.current.play();
            setIsPlaying(true);
            setIsLoading(false);

            audioRef.current.onended = () => {
              setIsPlaying(false);
              URL.revokeObjectURL(audioUrl);
            };
          }
        } catch (err) {
          console.error('[AudioNarration] Error processing audio data:', err);
          setError('Error processing audio data');
        }
      };

      eventSourceRef.current.onerror = (err) => {
        console.error('[AudioNarration] EventSource error:', err);
        setError('could not connect to audio service');
        setIsLoading(false);
        eventSourceRef.current?.close();
      };
    } catch (err) {
      console.error('[AudioNarration] Error starting audio narration:', err);
      setError(err.message || 'could not start audio narration');
      setIsLoading(false);
    }
  }, [historyAudioUrl]);

  const stopAudio = useCallback(() => {
    if (audioRef.current) {
      audioRef.current.pause();
      audioRef.current = null;
    }
    if (eventSourceRef.current) {
      eventSourceRef.current.close();
      eventSourceRef.current = null;
    }
    setIsPlaying(false);
    setIsLoading(false);
  }, []);

  const startNarration = useCallback(async (topicTitle, narrationMode = 'overview', extraParams = {}, locationData = null, selectedArticle = null) => {
    if (!topicTitle) {
      return;
    }

    const startedAt = performance.now();
    const baseExtraParams = extraParams && typeof extraParams === 'object' ? { ...extraParams } : {};
    const forcedCacheKey = (baseExtraParams.forceCacheKey || '').toString().trim();

    const period = (baseExtraParams?.period || '').toString().trim();
    if (narrationMode === 'historicPeriod' && !period) {
      setError('Missing period for historicPeriod mode');
      return;
    }

    const displayTitle = period ? `${topicTitle} ${period}` : topicTitle;

    titleRef.current = displayTitle;
    modeRef.current = narrationMode;
    setTitle(displayTitle);
    setMode(narrationMode);
    setError(null);

    const locationContext = buildAddressContext(locationData);
    const anecdoteMemoryKey = buildAnecdoteMemoryKey(topicTitle, languageCode, locationContext);
    const explicitTopicId = (baseExtraParams.topicId || '').toString().trim();
    const topicTranscriptMemoryKey = buildTopicTranscriptMemoryKey(
      topicTitle,
      languageCode,
      locationContext,
      selectedArticle,
      explicitTopicId
    );
    const knownTranscriptSnippets = buildKnownTranscriptSnippets(topicTranscriptMemoryKey);

    const isFreshAnecdoteRequest = narrationMode === 'anecdote' && !forcedCacheKey;
    if (isFreshAnecdoteRequest && !baseExtraParams.anecdoteRequestId) {
      baseExtraParams.anecdoteRequestId = `${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;
    }

    const priorAnecdotes = narrationMode === 'anecdote'
      ? getStoredAnecdotes(anecdoteMemoryKey)
      : [];

    const cacheKeyParams = { ...baseExtraParams };
    delete cacheKeyParams.priorAnecdotes;
    delete cacheKeyParams.forceCacheKey;

    if (knownTranscriptSnippets.length) {
      cacheKeyParams.knownTranscriptDigest = knownTranscriptSnippets.join('|').slice(0, 800);
    }

    const computedCacheKey = generateCacheKey(topicTitle, narrationMode, cacheKeyParams, languageCode);
    const cacheKey = forcedCacheKey || computedCacheKey;

    const promptExtraParams = {
      ...baseExtraParams,
      ...(narrationMode === 'anecdote' ? { priorAnecdotes } : {}),
      knownTranscriptSnippets,
      knownTranscriptSnippetsJson: knownTranscriptSnippets.length ? JSON.stringify(knownTranscriptSnippets) : undefined,
    };

    try {
      const logParams = forcedCacheKey
        ? { ...baseExtraParams, forceCacheKey: cacheKey }
        : baseExtraParams;
      logAudioCall(topicTitle, narrationMode, locationData, selectedArticle, logParams, languageCode);
    } catch (err) {
      console.error('[Audio] Failed to log audio call to history:', err);
    }

    const request = {
      requestId: `audio-${Date.now()}-${Math.random().toString(36).slice(2, 10)}`,
      startedAt,
      topicTitle,
      narrationMode,
      extraParams: promptExtraParams,
      displayTitle,
      cacheKey,
      languageCode,
      voice: DEFAULT_NARRATION_VOICE,
      anecdoteMemoryKey,
      topicTranscriptMemoryKey,
      priorAnecdotes,
      knownTranscriptSnippets,
      shouldBypassCache: isFreshAnecdoteRequest,
      topicCountry: locationContext.topicCountry,
      topicSecondMostZoomedOutFeature: locationContext.topicSecondMostZoomedOutFeature,
      prompt: buildNarrationPrompt({
        topicTitle,
        narrationMode,
        languageCode,
        topicCountry: locationContext.topicCountry,
        topicSecondMostZoomedOutFeature: locationContext.topicSecondMostZoomedOutFeature,
        period: promptExtraParams.period,
        priorAnecdotes: promptExtraParams.priorAnecdotes,
        knownTranscriptSnippets,
        cardTitle: promptExtraParams.cardTitle,
        cardBody: promptExtraParams.cardBody,
      }),
    };

    const userGeminiApiKey = readUserGeminiApiKey();

    let idToken = '';
    if (!userGeminiApiKey) {
      try {
        idToken = await loginMachine.getIdToken();
        if (!idToken) {
          throw new Error('Authentication required');
        }
        loginMachine.recordUsage('audio');
      } catch (err) {
        console.error('[Audio] Error fetching ID token:', err);
        setStatus('idle');
        window.dispatchEvent(new CustomEvent('quotaExceeded', {
          detail: { reason: 'auth_token_failed' },
        }));
        return;
      }
    }

    if (!request.shouldBypassCache) {
      try {
        const cacheComplete = await audioCache.isComplete(request.cacheKey);
        if (cacheComplete) {
          const cachedChunks = await audioCache.getChunks(request.cacheKey);
          if (cachedChunks.length) {
            stopSseStream(true);

            audioPlayer.resetAudioState();
            pausedChunkBufferRef.current = [];
            setStatus('playing');
            setIsPlaying(true);

            cachedChunks.forEach((buf) => audioPlayer.playPCMAudio(buf));
            audioPlayer.completeAudioStream();
            return;
          }
        }
      } catch (cacheErr) {
        console.error('[Audio] Cache lookup failed', cacheErr);
      }
    }

    setStatus('connecting');
    if (userGeminiApiKey) {
      startDirectGeminiNarration(request, userGeminiApiKey);
    } else {
      startSseNarration(request, idToken);
    }
  }, [languageCode, startDirectGeminiNarration, startSseNarration, stopSseStream]);

  const flushPausedChunks = useCallback(() => {
    const chunks = pausedChunkBufferRef.current;
    if (!chunks.length) {
      return;
    }

    for (const buf of chunks) {
      audioPlayer.playPCMAudio(buf);
    }
    pausedChunkBufferRef.current = [];
  }, []);

  const seekTo = useCallback((timeInSeconds) => {
    audioPlayer.seekTo(timeInSeconds);
    setCurrentTime(timeInSeconds);
  }, []);

  const togglePause = useCallback(() => {
    if (statusRef.current === 'playing') {
      audioPlayer.stopPlayback();
      setStatus('paused');
      return;
    }

    if (statusRef.current === 'paused') {
      const resumed = audioPlayer.resumePlayback();
      if (resumed) {
        flushPausedChunks();
        setStatus('playing');
      } else {
        stop();
      }
    }
  }, [flushPausedChunks, stop]);

  const togglePlayPause = useCallback(() => {
    if (statusRef.current === 'playing' || statusRef.current === 'paused') {
      togglePause();
    }
  }, [togglePause]);

  return {
    status,
    title,
    mode,
    startNarration,
    togglePause,
    togglePlayPause,
    stop,
    seekTo,
    isPlaying,
    isLoading,
    error,
    playAudioNarration,
    stopAudio,
    currentTime,
    duration,
  };
}

function base64ToBlob(base64, mimeType) {
  const byteCharacters = atob(base64);
  const byteNumbers = new Array(byteCharacters.length);
  for (let i = 0; i < byteCharacters.length; i += 1) {
    byteNumbers[i] = byteCharacters.charCodeAt(i);
  }
  const byteArray = new Uint8Array(byteNumbers);
  return new Blob([byteArray], { type: mimeType });
}
